import { useCallback, useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { InlineLoader } from "@/components/common/Loader";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { OPS } from "@/constants/testIds";
import { wmMessage } from "@/lib/wmError";

const C = OPS.wm.admin;

/**
 * «طلبات الأصناف» — the other half of the employees' «لم أجد الصنف» button.
 * An admin either points the request at an existing item (the requester gets a
 * notification telling them what to use) or declines it. Nothing is deleted.
 */
export function ItemRequestsAdmin({ onCountChange }) {
    const { t, lang } = useI18n();
    const [rows, setRows] = useState([]);
    const [items, setItems] = useState([]);
    const [pick, setPick] = useState({});
    const [status, setStatus] = useState("open");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [rq, it] = await Promise.all([
                api.get("/waste-meals/item-requests", { params: { status } }),
                api.get("/waste-meals/items", { params: { include_inactive: 0 } }),
            ]);
            setRows(rq.data.rows || []);
            setItems(it.data.rows || []);
            onCountChange && onCountChange(rq.data.open_count || 0);
        } catch (e) {
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        } finally {
            setLoading(false);
        }
    }, [status, lang, t, onCountChange]);

    useEffect(() => { load(); }, [load]);

    const resolve = async (req, next) => {
        if (next === "resolved" && !pick[req.id]) {
            toast.error(t("wm_admin_pick_item_first"));
            return;
        }
        setBusy(true);
        try {
            await api.patch(`/waste-meals/item-requests/${req.id}`, {
                status: next, resolved_item_id: next === "resolved" ? pick[req.id] : null,
            });
            toast.success(t(next === "resolved" ? "wm_admin_request_done" : "wm_admin_request_declined"));
            await load();
        } catch (e) {
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        } finally {
            setBusy(false);
        }
    };

    if (loading) return <InlineLoader />;

    return (
        <div className="space-y-3">
            <div className="flex gap-1 rounded-full border border-[color:var(--border-soft)] p-0.5 w-fit">
                {["open", "resolved", "rejected", "all"].map((s) => (
                    <button key={s} type="button" data-testid={C.requestFilter(s)} onClick={() => setStatus(s)}
                            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                                status === s
                                    ? "bg-[color:var(--brand-orange)] text-white"
                                    : "text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]"
                            }`}>
                        {t(`wm_admin_request_${s}`)}
                    </button>
                ))}
            </div>

            {!rows.length ? (
                <p data-testid={C.requestsEmpty}
                   className="rounded-[var(--radius-lg)] border border-dashed border-[color:var(--border-soft)] py-10 text-center text-sm text-[color:var(--ink-muted)]">
                    {t("wm_admin_requests_empty")}
                </p>
            ) : (
                <div className="space-y-2">
                    {rows.map((r) => (
                        <div key={r.id} data-testid={C.requestRow(r.id)}
                             className="rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="font-semibold text-[color:var(--ink)]">{r.item_text}</span>
                                <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                                    {lang === "ar" ? r.branch_name_ar : r.branch_name_en || r.branch_name_ar}
                                </Badge>
                                {r.type_hint ? (
                                    <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                                        {t(r.type_hint === "waste" ? "wm_tab_waste" : "wm_tab_meals")}
                                    </Badge>
                                ) : null}
                                <span className="text-[11px] text-[color:var(--ink-faint)]">
                                    {r.requested_by_name} · <bdi dir="ltr">{(r.created_at || "").slice(0, 16).replace("T", " ")}</bdi>
                                </span>
                                <Badge data-testid={C.requestStatus(r.id)}
                                       className={`ms-auto px-2 py-0 text-[10px] ${
                                           r.status === "open" ? "bg-[color:var(--warning-soft-fg)]" : ""}`}>
                                    {t(`wm_admin_request_${r.status}`)}
                                </Badge>
                            </div>
                            {r.note ? (
                                <p className="mt-1 text-xs text-[color:var(--ink-muted)]">{r.note}</p>
                            ) : null}
                            {r.resolved_item ? (
                                <p className="mt-1 text-xs font-semibold text-[color:var(--success)]">
                                    → {r.resolved_item.name_ar} (<bdi dir="ltr">{r.resolved_item.code}</bdi>)
                                </p>
                            ) : null}

                            {r.status === "open" ? (
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <Select value={pick[r.id] || ""}
                                            onValueChange={(v) => setPick((p) => ({ ...p, [r.id]: v }))}>
                                        <SelectTrigger data-testid={C.requestItemSelect(r.id)} className="h-9 w-60">
                                            <SelectValue placeholder={t("wm_admin_link_to_item")} />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-72">
                                            {items.map((i) => (
                                                <SelectItem key={i.id} value={i.id}
                                                            data-testid={C.requestItemOption(r.id, i.id)}>
                                                    {i.name_ar}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button data-testid={C.requestResolve(r.id)} size="sm" disabled={busy}
                                            onClick={() => resolve(r, "resolved")}
                                            className="h-9 gap-1.5 text-xs font-semibold">
                                        <Check className="h-4 w-4" />
                                        {t("wm_admin_request_link")}
                                    </Button>
                                    <Button data-testid={C.requestReject(r.id)} size="sm" variant="ghost" disabled={busy}
                                            onClick={() => resolve(r, "rejected")}
                                            className="h-9 gap-1.5 text-xs font-semibold">
                                        <X className="h-4 w-4" />
                                        {t("wm_admin_request_decline")}
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
